import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { VerseBanner } from "@/components/VerseBanner";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PlusCircle, ClipboardList, TrendingUp, BookMarked, Wand2, FileText, BookHeart, CalendarDays, BookOpen, Award, KeyRound, ListChecks, Trophy, SpellCheck, RotateCcw, AlertTriangle, Trash2, Youtube, GraduationCap, Library } from "lucide-react";

const SUBJECT_ORDER = ["English", "Math", "Science", "Bible", "Social Studies"];

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwChild, setPwChild] = useState(null);
  const [resetChild, setResetChild] = useState(null);
  const [resetting, setResetting] = useState(false);

  const loadStudents = () => api.get("/students").then((r) => setStudents(r.data));

  useEffect(() => {
    Promise.all([api.get("/students"), api.get("/assignments")])
      .then(([s, a]) => { setStudents(s.data); setAssignments(a.data); })
      .finally(() => setLoading(false));
  }, []);

  const doReset = async () => {
    if (!resetChild) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/students/${resetChild.id}/reset-grades`);
      toast.success(`Reset ${resetChild.name}'s work — cleared ${data.cleared} graded item${data.cleared === 1 ? "" : "s"}.`);
      setResetChild(null);
      await loadStudents();
    } catch {
      toast.error("Couldn't reset. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  const overall = (avg) => {
    const vals = Object.values(avg || {});
    return vals.length ? Math.round(vals.reduce((x, y) => x + y, 0) / vals.length) : null;
  };

  return (
    <div>
      <TopBar subtitle="Parent workspace" />
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-head text-4xl font-bold text-[#3D405B]">Good morning, {user?.first_name}</h1>
            <p className="text-[#6B7280] mt-1">{students.length} students · {assignments.length} assignments created</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button data-testid="parent-guide-btn" onClick={() => navigate("/parent/guide")}
              className="flex items-center gap-2 rounded-full border border-[#EAE6DF] bg-white px-5 py-3 font-semibold text-[#3D405B] hover:border-[#81B29A] transition-colors">
              How it works
            </button>
            <button data-testid="ai-studio-btn" onClick={() => navigate("/parent/studio")}
              className="flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-6 py-3 font-semibold hover:bg-[#2f3248] transition-colors">
              <Wand2 size={18} /> AI Studio
            </button>
            <button data-testid="create-assignment-btn" onClick={() => navigate("/parent/create")}
              className="flex items-center gap-2 rounded-full bg-[#E07A5F] text-white px-6 py-3 font-semibold hover:bg-[#c96851] transition-colors">
              <PlusCircle size={18} /> Create Assignment
            </button>
          </div>
        </div>

        <div className="mb-8"><VerseBanner tone="parent" /></div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { icon: ClipboardList, label: "Gradebook", desc: "Scores & AI feedback", to: "/parent/gradebook", tid: "tile-gradebook" },
            { icon: Trash2, label: "Manage Assignments", desc: "Edit or delete assignments", to: "/parent/manage-assignments", tid: "tile-manage-assignments" },
            { icon: Youtube, label: "Video Controls", desc: "Approve or block channels", to: "/parent/video-controls", tid: "tile-video-controls" },
            { icon: GraduationCap, label: "My Continuing Education", desc: "Your own courses & lessons", to: "/parent/continuing-education", tid: "tile-continuing-education" },
            { icon: FileText, label: "Report Cards", desc: "Printable transcripts", to: "/parent/report-card", tid: "tile-reportcard" },
            { icon: TrendingUp, label: "Skill Mastery", desc: "Topic-level insight", to: "/parent/mastery", tid: "tile-mastery" },
            { icon: GraduationCap, label: "Curriculum Overview", desc: "Subjects, quarters & standards", to: "/parent/curriculum-overview", tid: "tile-curriculum-overview" },
            { icon: Library, label: "Lesson Library", desc: "Reuse & assign saved lessons", to: "/parent/library", tid: "tile-library" },
            { icon: BookHeart, label: "Scripture Memory", desc: "Verses & streaks", to: "/parent/scripture", tid: "tile-scripture" },
            { icon: CalendarDays, label: "School Calendar", desc: "Due dates & events", to: "/parent/calendar", tid: "tile-calendar" },
            { icon: BookOpen, label: "Reading Plan", desc: "Daily NIV Bible plan", to: "/parent/reading-plan", tid: "tile-reading-plan" },
            { icon: Award, label: "Badges", desc: "Student achievements", to: "/parent/badges", tid: "tile-badges" },
            { icon: ListChecks, label: "Chores", desc: "Assign & track chores", to: "/parent/chores", tid: "tile-chores" },
            { icon: Trophy, label: "Leaderboard", desc: "This week's family stars", to: "/parent/leaderboard", tid: "tile-leaderboard" },
            { icon: SpellCheck, label: "Spelling Words", desc: "Custom Spelling Bee words", to: "/parent/spelling-words", tid: "tile-spelling" },
            { icon: BookMarked, label: "Student Manual", desc: "How-to guide for kids", to: "/parent/manual", tid: "tile-manual" },
            { icon: FileText, label: "Parent & Teacher Manual", desc: "How to run the classroom", to: "/parent/staff-manual", tid: "tile-staff-manual" },
            { icon: SpellCheck, label: "Spelling Wk 1 Guide", desc: "Teacher's guide for placement week", to: "/parent/guide/spelling-week-1", tid: "tile-spelling-guide" },
          ].map((t) => (
            <button key={t.label} data-testid={t.tid}
              onClick={() => t.to && navigate(t.to)}
              className="text-left rounded-xl bg-white border border-[#EAE6DF] p-6 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
              <t.icon size={22} className="text-[#E07A5F]" />
              <p className="font-head font-semibold text-lg mt-3 text-[#3D405B]">{t.label}</p>
              <p className="text-sm text-[#6B7280]">{t.desc}</p>
            </button>
          ))}
        </div>

        <h2 className="font-head text-lg font-semibold text-[#3D405B] mb-4">Students</h2>
        {loading ? <p className="text-[#6B7280]">Loading…</p> : (
          <div className="grid md:grid-cols-3 gap-4">
            {students.map((s, i) => (
              <motion.div key={s.id} data-testid={`student-card-${s.first_name}`}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl bg-white border border-[#EAE6DF] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-head font-bold text-xl text-[#3D405B]">{s.first_name}</p>
                    <p className="text-xs text-[#6B7280]">Grade {s.grade_level} · Reading level {s.reading_level}</p>
                    {s.badge_count != null && <p className="text-xs text-[#F4A261] font-semibold mt-0.5 flex items-center gap-1"><Award size={12} /> {s.badge_count} badges</p>}
                  </div>
                  <span className="stat text-2xl font-semibold text-[#81B29A]">{overall(s.averages) ?? "—"}{overall(s.averages) != null && "%"}</span>
                </div>
                <div className="mt-4 space-y-1.5">
                  {SUBJECT_ORDER.map((subj) => (
                    <div key={subj} className="flex items-center justify-between text-sm">
                      <span className="text-[#6B7280]">{subj}</span>
                      <span className="stat text-[#3D405B]">{s.averages?.[subj] != null ? `${s.averages[subj]}%` : "—"}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex gap-2">
                  <button data-testid={`view-work-${s.first_name}`} onClick={() => navigate(`/parent/gradebook?student=${s.id}`)}
                    className="flex-1 rounded-full border border-[#EAE6DF] py-2 text-sm hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors">
                    View gradebook
                  </button>
                  <button data-testid={`set-password-${s.first_name}`} onClick={() => setPwChild({ id: s.id, name: s.first_name })}
                    className="rounded-full border border-[#EAE6DF] px-3 py-2 text-sm hover:border-[#3D405B] transition-colors" title={`Set ${s.first_name}'s password`}>
                    <KeyRound size={15} />
                  </button>
                  <button data-testid={`reset-grades-${s.first_name}`} onClick={() => setResetChild({ id: s.id, name: s.first_name })}
                    className="rounded-full border border-[#EAE6DF] px-3 py-2 text-sm text-[#6B7280] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors" title={`Reset ${s.first_name}'s assignments & gradebook`}>
                    <RotateCcw size={15} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      {pwChild && <ChangePasswordModal mode="reset" studentId={pwChild.id} studentName={pwChild.name} onClose={() => setPwChild(null)} />}
      {resetChild && (
        <div className="fixed inset-0 z-50 grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !resetting && setResetChild(null)}>
          <div data-testid="reset-confirm-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#E07A5F]/15 text-[#E07A5F] mb-4"><AlertTriangle size={22} /></span>
            <h2 className="font-head text-2xl font-bold text-[#3D405B]">Reset {resetChild.name}'s work?</h2>
            <p className="text-[#6B7280] mt-2 leading-relaxed">This clears all of {resetChild.name}'s submitted answers and grades. Every assignment goes back to <b>Not Started</b> so they can do it fresh. The lessons themselves are kept. This can't be undone.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="reset-cancel-btn" onClick={() => setResetChild(null)} disabled={resetting}
                className="flex-1 rounded-full border border-[#EAE6DF] py-2.5 font-semibold text-[#3D405B] hover:border-[#3D405B] transition-colors disabled:opacity-60">
                Cancel
              </button>
              <button data-testid="reset-confirm-btn" onClick={doReset} disabled={resetting}
                className="flex-1 rounded-full bg-[#E07A5F] text-white py-2.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">
                {resetting ? "Resetting…" : "Reset now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
